import React, {useState, useEffect, useCallback} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TablePagination,
	CircularProgress,
	Typography,
	Box,
	Avatar,
	InputAdornment,
	makeStyles,
} from '@material-ui/core';
import {Search as SearchIcon, Add as AddIcon} from '@material-ui/icons';
import API from '../../../../network/dashboardAPI';
import QuickProductCreateModal from './QuickProductCreateModal';

const useStyles = makeStyles((theme) => ({
	dialogContent: {
		minHeight: 400,
		maxHeight: 600,
	},
	searchBox: {
		marginBottom: theme.spacing(2),
	},
	productImage: {
		width: 50,
		height: 50,
		borderRadius: 4,
	},
	productRow: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: '#f5f5f5',
		},
	},
	selectedRow: {
		backgroundColor: '#e3f2fd',
		'&:hover': {
			backgroundColor: '#bbdefb',
		},
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 300,
	},
	emptyState: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: 300,
		flexDirection: 'column',
	},
	priceText: {
		fontWeight: 500,
		color: theme.palette.primary.main,
	},
}));

const ProductSelectionModal = ({open, onClose, onSelectProduct, currentItem, mode = 'replace'}) => {
	const classes = useStyles();
	const [searchTerm, setSearchTerm] = useState('');
	const [products, setProducts] = useState([]);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [quantity, setQuantity] = useState(1);
	const [showCreateModal, setShowCreateModal] = useState(false);

	// Fetch products - also returns the fetched list so callers can act on it
	const fetchProducts = useCallback(async (search = '', pageNum = 0, limit = 10) => {
		setLoading(true);
		try {
			// Call your product search API
			const response = await API.searchProducts({
				search,
				page: pageNum + 1,
				limit,
			});

			if (response && response.success) {
				const items = response.data?.items || response.data || [];
				setProducts(items);
				setTotalCount(response.data?.total || response.total || 0);
				return items;
			}
			return [];
		} catch (error) {
			setProducts([]);
			return [];
		} finally {
			setLoading(false);
		}
	}, []);

	// Load products on mount and search change
	useEffect(() => {
		if (open) {
			fetchProducts(searchTerm, page, rowsPerPage);
		}
	}, [open, searchTerm, page, rowsPerPage, fetchProducts]);

	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
		setPage(0); // Reset to first page on search
	};

	const handleProductSelect = (product) => {
		setSelectedProduct(product);
	};

	const handleConfirm = () => {
		if (selectedProduct) {
			// For add mode, include quantity
			if (mode === 'add') {
				onSelectProduct(selectedProduct, quantity);
			} else {
				onSelectProduct(selectedProduct);
			}
			handleClose();
		}
	};

	const handleClose = () => {
		setSearchTerm('');
		setSelectedProduct(null);
		setPage(0);
		setQuantity(1);
		onClose();
	};

	const handleQuantityChange = (e) => {
		const value = parseInt(e.target.value) || 1;
		setQuantity(Math.max(1, value));
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleProductCreated = async (newProduct) => {
		// Auto-select the newly created product. The object from the create modal is
		// minimal (no vat_rate, no product_code), so prefer the row the search API
		// returns for it - that one carries every field the order item needs.
		setSelectedProduct(newProduct);

		let items;
		// Set search term to the product code
		if (newProduct.code || newProduct.product_code) {
			const productCode = newProduct.code || newProduct.product_code;
			setSearchTerm(productCode);
			setPage(0);

			// Search for the newly created product by its code
			items = await fetchProducts(productCode, 0, rowsPerPage);
		} else {
			// Refresh the product list to include the newly created product
			items = await fetchProducts(searchTerm, page, rowsPerPage);
		}

		const createdId = newProduct.item_id || newProduct.id;
		const match = items?.find(
			(p) => String(p.item_id || p.product_id) === String(createdId)
		);
		if (match) {
			setSelectedProduct(match);
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">
							{mode === 'add'
								? 'Add Product to Order'
								: `Replace Item: ${currentItem?.item_name || 'Select Product'}`
							}
						</Typography>
						<Button
							variant="outlined"
							color="primary"
							startIcon={<AddIcon/>}
							onClick={() => setShowCreateModal(true)}
							size="small"
						>
							Create Product
						</Button>
					</Box>
				</DialogTitle>

				<DialogContent className={classes.dialogContent}>
				{/* Quantity Input for Add Mode */}
				{mode === 'add' && (
					<Box style={{marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8}}>
						<Typography variant="body1" style={{fontWeight: 500}}>
							Quantity:
						</Typography>
						<TextField
							type="number"
							variant="outlined"
							size="small"
							value={quantity}
							onChange={handleQuantityChange}
							inputProps={{min: 1, style: {width: 80}}}
						/>
					</Box>
				)}

				{/* Search Box */}
				<TextField
					fullWidth
					variant="outlined"
					placeholder="Search products by name or code..."
					value={searchTerm}
					onChange={handleSearchChange}
					className={classes.searchBox}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon/>
							</InputAdornment>
						),
					}}
				/>

				{/* Products Table */}
				{loading ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress/>
					</Box>
				) : products.length === 0 ? (
					<Box className={classes.emptyState}>
						<Typography variant="h6" color="textSecondary">
							No products found
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Try adjusting your search
						</Typography>
					</Box>
				) : (
					<>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Image</TableCell>
									<TableCell>Product Code</TableCell>
									<TableCell>Product Name</TableCell>
									<TableCell>Price</TableCell>
									<TableCell>Stock</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{products.map((product) => (
									<TableRow
										key={product.item_id || product.product_id}
										onClick={() => handleProductSelect(product)}
										className={
											selectedProduct?.item_id === product.item_id
												? classes.selectedRow
												: classes.productRow
										}
									>
										<TableCell>
											<Avatar
												src={product.default_image_url || product.image_url}
												alt={product.name || product.product_name}
												className={classes.productImage}
												variant="rounded"
											/>
										</TableCell>
										<TableCell>{product.product_code}</TableCell>
										<TableCell>
											<Typography variant="body2">
												{product.name || product.product_name}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography className={classes.priceText}>
												${(product.price || product.item_price || 0).toFixed(2)}
											</Typography>
										</TableCell>
										<TableCell>
											{product.stock_quantity !== undefined
												? product.stock_quantity
												: 'N/A'}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						<TablePagination
							component="div"
							count={totalCount}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[5, 10, 25, 50]}
						/>
					</>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={handleClose} color="default">
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					color="primary"
					variant="contained"
					disabled={!selectedProduct}
				>
					{mode === 'add'
						? `Add Product (Qty: ${quantity})`
						: 'Replace with Selected Product'
					}
				</Button>
			</DialogActions>
		</Dialog>

			{/* Quick Product Create Modal */}
			<QuickProductCreateModal
				open={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				onProductCreated={handleProductCreated}
			/>
		</>
	);
};

export default ProductSelectionModal;
